import {
  Box,
  Button,
  HStack,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Text,
  VStack
} from '@chakra-ui/react';
import { ReactNode, SetStateAction, useState } from 'react';
import { CustomPicker } from 'react-color';
import {
  EditableInput,
  Hue,
  Saturation
} from 'react-color/lib/components/common';

import { AddColorIcon } from '../../../assets/icons/AddColorIcon';
import { EyedropperIcon } from '../../../assets/icons/EyedropperIcon';
// import ColorPicker from './ColorPicker';

type ColorPaletteProps = {
  children?: ReactNode;
  setCurrentColor: React.Dispatch<React.SetStateAction<string>>;
  initialPickerColor: string;
};

export const ColorPicker = (props: any) => {
  const { hex, customColors, setCustomColors, onChange, onCancelar } = props;
  return (
    <Box m="0 !important">
      <Box p="10.7px" borderBottom="1px solid #D9D9D9">
        <Box w="247.48px" h="225.52px" position="relative">
          <Saturation {...props} />
        </Box>
      </Box>

      <Box p="8px 10.7px">
        <HStack w="100%" justifyContent="space-between" alignItems="center">
          <Box
            position="relative"
            h="7.85px"
            w="220px"
            borderRadius="4px"
            overflow="hidden"
          >
            <Hue {...props} />
          </Box>
          <Box>
            <EyedropperIcon />
          </Box>
        </HStack>
      </Box>

      <Box p="5px 20px">
        <HStack justifyContent="space-between">
          <Text fontSize="0.87rem">Hexadecimal</Text>
          <Box w="26px" h="26px" borderRadius="50%" bg={hex} />
          <EditableInput
            style={{
              input: {
                width: '87px',
                height: '50px',
                border: `1px solid ${hex}`,
                paddingLeft: '10px',
                borderRadius: '6px',
                fontSize: '0.87rem'
              }
            }}
            value={hex}
            onChange={onChange}
          />
        </HStack>
      </Box>

      <Box mt="20px !important" pb="20px" pl="20px" pr="20px">
        <HStack w="100%" justifyContent="space-between">
          <Button
            bg="transparent"
            w="120px"
            h="48px"
            border="1px solid #001E64"
            borderRadius="133.5px"
            color="#001E64"
            _hover={{}}
            sx={{
              '&:focus': {
                boxShadow: 'none !important'
              }
            }}
            onClick={() => onCancelar(false)}
          >
            Cancelar
          </Button>
          <Button
            bg="#FF5000"
            w="100px"
            h="48px"
            borderRadius="133.5px"
            color="white"
            _hover={{}}
            sx={{
              '&:focus': {
                boxShadow: 'none !important'
              }
            }}
            onClick={() => {
              customColors.push(hex);
              setCustomColors([...customColors]);
              onCancelar(false);
            }}
          >
            Ok
          </Button>
        </HStack>
      </Box>
    </Box>
  );
};

export const CustomColorPicker = CustomPicker(ColorPicker);

export const ColorPalette = ({
  children,
  setCurrentColor,
  initialPickerColor
}: ColorPaletteProps): JSX.Element => {
  const [isCustomColorScreenOpen, setCustomColorScreenOpen] = useState(false);
  const [color, setColor] = useState(initialPickerColor);
  const [customColors, setCustomColors] = useState([]);
  const colors = {
    sulamerica: [
      '#FF5000',
      '#FF8C00',
      '#001E64',
      '#146CFA',
      '#00E1B8',
      '#9632FA',
      '#2A4E9A',
      '#AA2C23',
      '#006217',
      '#E59401'
    ],
    custom: customColors,
    default: [
      '#000000',
      '#434343',
      '#666666',
      '#999999',
      '#B7B7B7',
      '#CCCCCC',
      '#DAD9D9',
      '#EFEFEE',
      '#F3F3F4',
      '#FFFFFF',
      '#E60000',
      '#E65100',
      '#F57F17',
      '#33691E',
      '#004D40',
      '#01579B',
      '#1A237E',
      '#4A148C',
      '#B71C66',
      '#B71C1C',
      '#F50000',
      '#F57C00',
      '#FBC02D',
      '#689F38',
      '#00796B',
      '#0288D1',
      '#303F9F',
      '#7B1FA2',
      '#D32F7E',
      '#D32F2F',
      '#FB0000',
      '#FB8C00',
      '#FDD835',
      '#7CB342',
      '#00897B',
      '#039BE5',
      '#3949AB',
      '#8E24AA',
      '#E53594',
      '#E53935',
      '#FF0000',
      '#FF9800',
      '#FFEB3B',
      '#8BC34A',
      '#009688',
      '#03A9F4',
      '#3F51B5',
      '#9C27B0',
      '#F43691',
      '#F44336',
      '#FF2626',
      '#FFA726',
      '#FFEE58',
      '#9CCC65',
      '#26A69A',
      '#29B6F6',
      '#5C6BC0',
      '#AB47BC',
      '#EF50A6',
      '#EF5350',
      '#FF4D4D',
      '#FFB74D',
      '#FFF176',
      '#AED581',
      '#4DB6AC',
      '#4FC3F7',
      '#7986CB',
      '#BA68C8',
      '#E573AA',
      '#E57373',
      '#FF8080',
      '#FFCC80',
      '#FFF59D',
      '#C5E1A5',
      '#80CBC4',
      '#81D4FA',
      '#9FA8DA',
      '#CE93D8',
      '#EF9ABE',
      '#EF9A9A'
    ]
  };

  return (
    <Popover
      placement="right"
      offset={[40, -215]}
      onOpen={() => {
        setCustomColorScreenOpen(false);
      }}
    >
      <PopoverTrigger>{children}</PopoverTrigger>
      <PopoverContent
        borderRadius="24px"
        p="0"
        w="fit-content"
        sx={{
          '&:focus': {
            boxShadow: 'none !important'
          }
        }}
      >
        {isCustomColorScreenOpen ? <PopoverCloseButton top="10px" /> : null}

        <PopoverBody
          p={isCustomColorScreenOpen ? '0' : '20px'}
          w="fit-content"
          maxW="fit-content"
          boxShadow="0px 4px 4px rgba(0, 0, 0, 0.25) !important"
          borderRadius="24px"
        >
          {!isCustomColorScreenOpen ? (
            <VStack alignItems="start" gap="10px">
              <Box w="100%">
                <Text fontSize="0.875rem" fontWeight="bold">
                  Cores SulAmérica
                </Text>
                <HStack
                  gap="2px"
                  borderBottom="1px solid #F4F4F4"
                  m="0 !important"
                  pb="10px"
                  pt="10px"
                >
                  {colors.sulamerica.map((color: string) => (
                    <Box
                      key={color}
                      w="20px"
                      h="20px"
                      bg={color}
                      borderRadius="50%"
                      ml="0 !important"
                      cursor="pointer"
                      onClick={() => {
                        setCurrentColor(color);
                      }}
                    />
                  ))}
                </HStack>
              </Box>
              <Box w="100%">
                <Text fontSize="0.875rem" fontWeight="bold">
                  Cores Personalizadas
                </Text>
                <HStack
                  gap="2px"
                  borderBottom="1px solid #F4F4F4"
                  m="0 !important"
                  pb="10px"
                  pt="10px"
                >
                  {colors.custom.length > 0 ? (
                    <>
                      {colors.custom.map((color: string) => (
                        <Box
                          key={color}
                          w="20px"
                          h="20px"
                          bg={color}
                          borderRadius="50%"
                          ml="0 !important"
                          cursor="pointer"
                          onClick={() => {
                            setCurrentColor(color);
                          }}
                        />
                      ))}
                      <AddColorIcon
                        w="1.25rem"
                        h="1.25rem"
                        cursor="pointer"
                        m="0 !important"
                        onClick={() => {
                          setCustomColorScreenOpen(true);
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <AddColorIcon
                        w="1.25rem"
                        h="1.25rem"
                        cursor="pointer"
                        onClick={() => {
                          setCustomColorScreenOpen(true);
                        }}
                      />
                      <Box>
                        <Text fontSize="0.75rem">
                          Adicione suas <Box as="br" /> cores personalizadas
                        </Text>
                      </Box>
                    </>
                  )}
                </HStack>
              </Box>
              <Box w="100%">
                <Text fontSize="0.875rem" fontWeight="bold">
                  Cores SulAmérica
                </Text>
                <HStack
                  gap="2px"
                  maxW="220px"
                  borderBottom="1px solid #F4F4F4"
                  m="0 !important"
                  pb="10px"
                  pt="10px"
                  wrap="wrap"
                >
                  {colors.default.map((color) => (
                    <Box
                      key={color}
                      w="20px"
                      h="20px"
                      bg={color}
                      borderRadius="50%"
                      ml="0 !important"
                      cursor="pointer"
                      onClick={() => {
                        setCurrentColor(color);
                      }}
                    />
                  ))}
                </HStack>
              </Box>
            </VStack>
          ) : (
            <VStack width="100%">
              <Box
                padding="15px 10px"
                borderBottom="1px solid #D9D9D9"
                width="100%"
              >
                <Text
                  fontWeight="bold"
                  fontSize="0.875rem"
                  width="100%"
                  textAlign="left"
                >
                  Cores Personalizadas
                </Text>
              </Box>
              <Box m="0 !important">
                <CustomColorPicker
                  color={color}
                  customColors={customColors}
                  onCancelar={setCustomColorScreenOpen}
                  onChange={(updatedColor: SetStateAction<string>) =>
                    setColor(updatedColor)
                  }
                  setCustomColors={setCustomColors}
                />
              </Box>
            </VStack>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
